import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import AnnouncementIcon from "@icons/announcement-03.svg?react";
import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";

import { ReleaseNotes } from "@/common/utils/types";
import ReleaseNotesBody from "../../common/components/ReleaseNotesBody";
import { NAV_ICON_BUTTON_CLASS, NAV_ICON_CLASS } from "./navIconButton";
import { getLatestReleaseNotes } from "../../common/lib/api";

const ReleaseNotesMenu: React.FC = () => {
    const [releaseNotes, setReleaseNotes] = useState<ReleaseNotes | null>(null);
    const navigate = useNavigate();

    // 3days old
    //
    // Measured from created_at (when the note was published) rather than
    // release_date (the date printed on the card). Now that Spork's editor sets
    // release_date from a datepicker, writing up a release after the fact and
    // backdating it more than three days would publish a note the bell never
    // announces at all. created_at is what "new since you last looked" means
    // here; release_date still drives ordering and display (EN-956).
    //
    // Falls back to release_date because release_notes.created_at is nullable
    // (NOW() default, no NOT NULL). Without the fallback a null-created_at row
    // would silently lose a badge it gets today.
    const publishedAt = releaseNotes?.created_at ?? releaseNotes?.release_date;
    const isNewestReleaseLessThanThreeDaysOld =
        publishedAt && new Date(new Date(publishedAt).getTime() + 3 * 24 * 60 * 60 * 1000) > new Date(Date.now());

    useEffect(() => {
        const fetchReleaseNotes = async () => {
            try {
                const response = await getLatestReleaseNotes();
                if (response) {
                    setReleaseNotes(response);
                }
            } catch {
                toast.error("Error fetching release notes");
            }
        };
        fetchReleaseNotes();
    }, []);

    return (
        <Fragment>
            <Menu as="div" className="relative inline-block text-left">
                <div className="relative">
                    <MenuButton
                        className={`${NAV_ICON_BUTTON_CLASS} relative`}
                        title="Release notes"
                        aria-label="Release notes"
                    >
                        <AnnouncementIcon className={NAV_ICON_CLASS} />
                        {isNewestReleaseLessThanThreeDaysOld && (
                            <div
                                style={{ top: "6px", right: "6px" }}
                                className="absolute h-2.5 w-2.5 bg-gray-100 rounded-full flex items-center justify-center"
                            >
                                <div className="h-3/4 w-3/4 bg-max-500 rounded-full" />
                            </div>
                        )}
                    </MenuButton>
                </div>
                <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                >
                    <MenuItems
                        modal={false}
                        className="absolute right-0 mt-2 w-80 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none"
                    >
                        <div className="p-4 overflow-y-auto max-h-80">
                            <MenuItem>
                                {releaseNotes === null ? (
                                    <div className="px-4 py-2 text-sm text-gray-500">No new releases to show</div>
                                ) : (
                                    // div needed because HeadlessUI wants a div to pass its implict ref to
                                    <div>
                                        <ReleaseNotesBody items={releaseNotes.items} />
                                    </div>
                                )}
                            </MenuItem>
                        </div>
                        <div className="p-1">
                            <MenuItem>
                                {({ focus }) => (
                                    <button
                                        onClick={() => navigate("/changelog")}
                                        className={`${focus ? "bg-max-900 text-white" : "text-gray-700"}
                      group flex w-full items-center justify-end px-2 py-2 text-sm rounded-md`}
                                    >
                                        View Changelog
                                    </button>
                                )}
                            </MenuItem>
                        </div>
                    </MenuItems>
                </Transition>
            </Menu>
        </Fragment>
    );
};

export default ReleaseNotesMenu;
